import { z } from "zod";
import { isUtcInstant } from "../migrations/types.ts";

/** Frozen written-policy version for version-2 bytes. Do not follow later domain constants. */
const V2_SCHEDULER_POLICY_VERSION = 1;

/** Frozen copy of version-2 word types. Do not reuse a later moving domain constant here. */
export const V2_WORD_TYPES = [
  "noun",
  "verb",
  "phrasal_verb",
  "adjective",
  "adverb",
  "phrase",
  "idiom",
  "other",
] as const;

export const vocabularySnapshotV2WordTypeSchema = z.enum(V2_WORD_TYPES);

const utcInstantSchema = z.string().refine(isUtcInstant, {
  message: "Must be a UTC instant",
});

export const vocabularySnapshotV2WordEntrySchema = z.object({
  id: z.string().min(1),
  normalizedText: z.string().min(1),
  displayText: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

const v2RelatedDistinctionSchema = z.object({
  meaningId: z.string().min(1),
  explanation: z.string(),
});

const v2ExerciseBase = {
  id: z.string().min(1),
  question: z.string().min(1),
  acceptedAnswers: z.array(z.string().min(1)).min(1),
  correctAnswer: z.string().min(1),
  explanation: z.string(),
  referencedMeaningIds: z.array(z.string()),
  relatedWordDistinctions: z.array(v2RelatedDistinctionSchema),
};

export const vocabularySnapshotV2ExerciseKindSchema = z.enum([
  "context_recall",
  "meaning_to_word",
  "confusable_context",
]);

export const vocabularySnapshotV2ExerciseSchema = z.discriminatedUnion("kind", [
  z.object({ ...v2ExerciseBase, kind: z.literal("context_recall") }),
  z.object({ ...v2ExerciseBase, kind: z.literal("meaning_to_word") }),
  z.object({
    ...v2ExerciseBase,
    kind: z.literal("confusable_context"),
    options: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) })).min(2).max(4),
    correctOptionId: z.string().min(1),
  }),
]);

export const vocabularySnapshotV2MeaningCardSchema = z.object({
  id: z.string().min(1),
  wordId: z.string().min(1),
  type: vocabularySnapshotV2WordTypeSchema,
  shortLabel: z.string().min(1),
  definition: z.string().min(1),
  russianHint: z.string().nullable(),
  example: z.string().min(1),
  memoryKey: z.string().nullable(),
  collocations: z.array(z.string()),
  contentRevision: z.number().int().nonnegative(),
  practiceReadiness: z.enum(["ready", "needs_regeneration"]),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});

export const vocabularySnapshotV2MeaningRelationSchema = z.object({
  sourceMeaningId: z.string().min(1),
  targetMeaningId: z.string().min(1),
  explanation: z.string().min(1),
  revision: z.number().int().nonnegative(),
});

export const vocabularySnapshotV2ExerciseSetSchema = z.object({
  meaningId: z.string().min(1),
  sourceContentRevision: z.number().int().nonnegative(),
  dependencyRevisions: z.record(z.string(), z.number().int().nonnegative()),
  generationMetadata: z.object({
    model: z.string(),
    generatedAt: z.string(),
    promptVersion: z.string(),
  }),
  exercises: z.array(vocabularySnapshotV2ExerciseSchema),
  status: z.enum(["ready", "invalidated"]),
});

export const vocabularySnapshotV2StageSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);

export const vocabularySnapshotV2PracticeStateSchema = z.object({
  meaningId: z.string().min(1),
  stage: vocabularySnapshotV2StageSchema,
  dueAt: utcInstantSchema,
  lastExposureAt: utcInstantSchema.nullable(),
  lastAssessmentAt: utcInstantSchema.nullable(),
  lastExerciseId: z.string().min(1).nullable(),
  stateRevision: z.number().int().nonnegative(),
  flaggedExerciseIds: z.array(z.string().min(1)),
});

export const vocabularySnapshotV2SchedulerStateSchema = z.object({
  schedulerVersion: z.literal(V2_SCHEDULER_POLICY_VERSION),
  lastPracticedMeaningId: z.string().min(1).nullable(),
  lastObservedAt: utcInstantSchema.nullable(),
});

export const vocabularySnapshotV2ScheduleSnapshotSchema = z.object({
  stage: vocabularySnapshotV2StageSchema,
  dueAt: utcInstantSchema,
  lastExposureAt: utcInstantSchema.nullable(),
  lastAssessmentAt: utcInstantSchema.nullable(),
  lastExerciseId: z.string().min(1).nullable(),
  stateRevision: z.number().int().nonnegative(),
});

export const vocabularySnapshotV2ReviewEventSchema = z.object({
  eventId: z.string().min(1),
  sessionId: z.string().min(1),
  meaningId: z.string().min(1),
  exerciseId: z.string().min(1).nullable(),
  exerciseKind: vocabularySnapshotV2ExerciseKindSchema.nullable(),
  meaningContentRevision: z.number().int().nonnegative(),
  expectedStateRevision: z.number().int().nonnegative(),
  presentedAt: utcInstantSchema,
  completedAt: utcInstantSchema,
  eventKind: z.enum(["assessment", "supporting", "exposure", "invalidation"]),
  firstAttemptOutcome: z.enum(["correct", "incorrect", "unknown"]).nullable(),
  hintUsed: z.boolean(),
  scheduledOrExtra: z.enum(["scheduled", "extra"]),
  schedulerVersion: z.literal(V2_SCHEDULER_POLICY_VERSION),
  previousSchedule: vocabularySnapshotV2ScheduleSnapshotSchema,
  resultingSchedule: vocabularySnapshotV2ScheduleSnapshotSchema,
});

export const vocabularySnapshotV2Schema = z
  .object({
    schemaVersion: z.literal(2),
    dataRevision: z.number().int().nonnegative(),
    schedulerState: vocabularySnapshotV2SchedulerStateSchema,
    words: z.array(vocabularySnapshotV2WordEntrySchema),
    meanings: z.array(vocabularySnapshotV2MeaningCardSchema),
    relations: z.array(vocabularySnapshotV2MeaningRelationSchema),
    exerciseSets: z.array(vocabularySnapshotV2ExerciseSetSchema),
    practiceStates: z.array(vocabularySnapshotV2PracticeStateSchema),
    reviewHistory: z.array(vocabularySnapshotV2ReviewEventSchema),
    historyTruncated: z.boolean(),
  })
  .strict()
  .superRefine((snapshot, ctx) => {
    const meaningIds = new Set(snapshot.meanings.map((meaning) => meaning.id));
    const wordIds = new Set(snapshot.words.map((word) => word.id));
    const practiceIds = snapshot.practiceStates.map((state) => state.meaningId);
    if (new Set(practiceIds).size !== practiceIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["practiceStates"],
        message: "Practice states must be unique per meaning.",
      });
    }
    for (const meaning of snapshot.meanings) {
      if (!wordIds.has(meaning.wordId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["meanings"],
          message: `Meaning ${meaning.id} references missing word ${meaning.wordId}.`,
        });
      }
    }
    for (const state of snapshot.practiceStates) {
      if (!meaningIds.has(state.meaningId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["practiceStates"],
          message: `Practice state refers to missing meaning ${state.meaningId}.`,
        });
      }
    }
    if (
      snapshot.schedulerState.lastPracticedMeaningId &&
      !meaningIds.has(snapshot.schedulerState.lastPracticedMeaningId)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["schedulerState", "lastPracticedMeaningId"],
        message: "lastPracticedMeaningId must reference a saved meaning.",
      });
    }
    const eventIds = snapshot.reviewHistory.map((event) => event.eventId);
    if (new Set(eventIds).size !== eventIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reviewHistory"],
        message: "Review event IDs must be unique.",
      });
    }
  });

export type VocabularySnapshotV2 = z.infer<typeof vocabularySnapshotV2Schema>;
